/**
 * useRunTimeline — durable run timeline for a session.
 *
 * Loads persisted `cortex_run_events` rows (written by the brain's journal)
 * and folds them through `reduceJournalRow`, then subscribes to Realtime
 * INSERTs so the timeline stays live without an open SSE stream (and merges
 * cleanly with one — `(run_id, seq)` dedupe makes double-delivery harmless).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  createTimeline,
  reduceJournalRow,
  type CortexTimeline,
  type RunEventRow,
  type TimelineRun,
} from "./cortexStreamReducer";

const PAGE = 500;

export interface UseRunTimelineResult {
  timeline: CortexTimeline;
  /** Ordered runs for rendering. */
  runs: TimelineRun[];
  isLoading: boolean;
}

export function useRunTimeline(sessionId: string | undefined): UseRunTimelineResult {
  const [timeline, setTimeline] = useState<CortexTimeline>(() => createTimeline());
  const [isLoading, setIsLoading] = useState(false);
  // (run_id:seq) dedupe across initial load + realtime inserts.
  const seenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!sessionId || !supabase) {
      setTimeline(createTimeline());
      return;
    }

    let cancelled = false;
    seenRef.current = new Set();
    setTimeline(createTimeline());
    setIsLoading(true);

    (async () => {
      try {
        const { data, error } = await (supabase as any)
          // Table is new (migration 20261014000000) — untyped until regen.
          .from("cortex_run_events")
          .select("run_id, session_id, seq, kind, payload, occurred_at")
          .eq("session_id", sessionId)
          .order("run_id", { ascending: true })
          .order("seq", { ascending: true })
          .limit(PAGE);

        if (cancelled) return;
        if (error) {
          // Table may not exist yet — degrade silently to live-only mode.
          setIsLoading(false);
          return;
        }

        let tl = createTimeline();
        for (const row of (data ?? []) as RunEventRow[]) {
          const key = `${row.run_id}:${row.seq}`;
          if (seenRef.current.has(key)) continue;
          seenRef.current.add(key);
          tl = reduceJournalRow(tl, row);
        }
        setTimeline(tl);
      } catch {
        // Non-fatal — timeline simply starts empty.
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    const channel = supabase
      .channel(`cortex_run_events:${sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "cortex_run_events",
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          const row = payload.new as RunEventRow;
          const key = `${row.run_id}:${row.seq}`;
          if (seenRef.current.has(key)) return;
          seenRef.current.add(key);
          setTimeline((prev) => reduceJournalRow(prev, row));
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      void supabase.removeChannel(channel).catch(() => { /* safety */ });
    };
  }, [sessionId]);

  const runs = useMemo(
    () => timeline.order.map((id) => timeline.byId[id]),
    [timeline],
  );

  return { timeline, runs, isLoading };
}
